import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, ShieldX, AlertTriangle, GraduationCap, Search, Loader2, Home,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Verify() {
  const { key } = useParams();
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkedAt, setCheckedAt] = useState(null);

  const doVerify = async (k) => {
    if (!k) return;
    setLoading(true);
    setData(null);
    try {
      const { data: res } = await axios.get(`${API}/public/verify`, {
        params: { key: k },
      });
      setData(res);
    } catch {
      setData({ found: false });
    } finally {
      setCheckedAt(new Date());
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) doVerify(key);
     
  }, [key]);

  const found = data?.found;
  const status = data?.status || "Active";
  const instituteName = data?.institute?.name || "Lovely Institute";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* header */}
      <div className="bg-gradient-to-br from-blue-950 via-blue-900 to-blue-700 text-white">
        <div className="max-w-3xl mx-auto px-6 pt-5 flex items-center justify-between">
          <Link
            to="/"
            data-testid="verify-home-btn"
            className="inline-flex items-center gap-1.5 text-sm font-medium bg-white/10 hover:bg-white/20 backdrop-blur px-4 py-2 rounded-full transition-soft"
          >
            <Home size={15} /> Home
          </Link>
          <Link
            to="/contact"
            data-testid="verify-contact-link"
            className="text-blue-200 hover:text-white text-xs uppercase tracking-widest transition-soft"
          >
            Contact Us
          </Link>
        </div>
        <div className="max-w-3xl mx-auto px-6 py-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-white/10 backdrop-blur mb-4">
            <GraduationCap size={30} />
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">{instituteName}</h1>
          <p className="text-blue-200 text-sm mt-1 uppercase tracking-widest">
            Certificate Verification
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 -mt-8 pb-16">
        {/* search box */}
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <label className="text-sm font-medium text-slate-700">
            Enter Registration / Enrollment / Certificate Number
          </label>
          <div className="mt-2 flex gap-2">
            <Input
              data-testid="verify-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doVerify(input.trim())}
              placeholder="e.g., 202600101 or LI-2026-0001"
              className="h-11"
            />
            <Button
              data-testid="verify-btn"
              onClick={() => doVerify(input.trim())}
              disabled={loading}
              className="h-11 bg-blue-800 hover:bg-blue-900 px-6"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} className="mr-2" />}
              Verify Certificate
            </Button>
          </div>
        </div>

        {/* result */}
        {data && (
          <div className="mt-6" data-testid="verify-result">
            {!found && (
              <StatusCard
                tone="red"
                icon={<ShieldX size={40} />}
                title="CERTIFICATE NOT FOUND"
                testId="verify-not-found"
                message="Sorry, no certificate record was found for this Registration Number. Please check the number and try again."
              />
            )}
            {found && status !== "Active" && (
              <StatusCard
                tone="amber"
                icon={<AlertTriangle size={40} />}
                title={`CERTIFICATE ${status.toUpperCase()}`}
                testId="verify-revoked"
                message="According to institute records, this certificate is no longer valid."
              />
            )}
            {found && status === "Active" && (
              <StatusCard
                tone="green"
                icon={<ShieldCheck size={40} />}
                title="VERIFIED"
                testId="verify-success"
                message="Certificate details match the official institute records."
              />
            )}

            {found && (
              <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="font-display text-lg font-semibold text-slate-900">
                    Certificate Information
                  </h2>
                  <span
                    data-testid="verify-type"
                    className={`text-xs uppercase tracking-wide px-2 py-1 rounded ${
                      data.format === 2
                        ? "bg-amber-100 text-amber-800"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {data.format === 2 ? "Marksheet" : "Diploma / Certificate"}
                  </span>
                </div>

                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <Group title="Student Details">
                    <Item label="Name" value={data.student_name} testId="v-name" />
                    <Item label="Father's Name" value={data.father_name} />
                    <Item label="Mother's Name" value={data.mother_name} />
                  </Group>
                  <Group title="Academic Details">
                    <Item label="Course" value={data.course_name} testId="v-course" />
                    <Item label="Duration" value={data.course_duration} />
                    <Item label="Session" value={data.session} />
                    <Item label="Grade" value={data.grade ? `'${data.grade}'` : "—"} />
                    {data.format === 2 && data.percentage != null && (
                      <Item label="Percentage" value={`${data.percentage}%`} />
                    )}
                    {data.format === 2 && data.result && (
                      <Item label="Result" value={data.result} testId="v-result" />
                    )}
                  </Group>
                  <Group title="Certificate Details">
                    <Item label="Registration No." value={data.registration_number} testId="v-reg" />
                    <Item label="Enrollment No." value={data.enrollment_number || "—"} />
                    <Item label="Certificate No." value={data.certificate_number} />
                    <Item label="Issue Date" value={data.issue_date} />
                    <Item label="Institute / Study Centre" value={data.institute_name || instituteName} />
                    <Item label="Certificate Status" value={status} testId="v-status" />
                  </Group>
                </div>

                {/* marks table for marksheet */}
                {data.format === 2 && data.marks && data.marks.length > 0 && (
                  <div className="px-6 pb-6">
                    <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
                      Marks Statement
                    </h3>
                    <table className="w-full text-sm border border-slate-200" data-testid="v-marks-table">
                      <thead>
                        <tr className="bg-blue-900 text-white text-left">
                          <th className="px-3 py-2 font-medium">Subject</th>
                          <th className="px-3 py-2 font-medium text-center w-20">Total</th>
                          <th className="px-3 py-2 font-medium text-center w-24">Min. Marks</th>
                          <th className="px-3 py-2 font-medium text-center w-28">Marks Obtained</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.marks.map((m, i) => (
                          <tr key={`${m.subject}-${i}`} className="border-t border-slate-200">
                            <td className="px-3 py-2">{m.subject}</td>
                            <td className="px-3 py-2 text-center">{m.total}</td>
                            <td className="px-3 py-2 text-center">{m.min}</td>
                            <td className="px-3 py-2 text-center">{m.obtained}</td>
                          </tr>
                        ))}
                        <tr className="border-t border-slate-300 bg-blue-50 font-semibold">
                          <td className="px-3 py-2">GRAND TOTAL</td>
                          <td className="px-3 py-2 text-center">{data.total_marks}</td>
                          <td className="px-3 py-2 text-center"></td>
                          <td className="px-3 py-2 text-center">{data.marks_obtained}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 text-xs text-slate-500 flex flex-wrap justify-between gap-2">
                  <span>
                    This certificate has been verified against the official records of the institute.
                  </span>
                  {checkedAt && (
                    <span data-testid="verify-time">
                      Verified at: {checkedAt.toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusCard({ tone, icon, title, message, testId }) {
  const tones = {
    green: "bg-green-50 border-green-200 text-green-800",
    red: "bg-red-50 border-red-200 text-red-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
  };
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border p-6 flex items-center gap-4 ${tones[tone]}`}
    >
      <div className="shrink-0">{icon}</div>
      <div>
        <div className="font-display text-xl font-bold flex items-center gap-2">
          {tone === "green" && <span>✓</span>}
          {tone === "red" && <span>✕</span>}
          {tone === "amber" && <span>⚠</span>}
          {title}
        </div>
        <p className="text-sm mt-1 opacity-80">{message}</p>
      </div>
    </div>
  );
}

const Group = ({ title, children }) => (
  <div>
    <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
      {title}
    </h3>
    <dl className="space-y-1.5">{children}</dl>
  </div>
);

const Item = ({ label, value, testId }) => (
  <div className="flex justify-between gap-3 text-sm">
    <dt className="text-slate-500">{label}</dt>
    <dd data-testid={testId} className="font-medium text-slate-900 text-right">
      {value || "—"}
    </dd>
  </div>
);
